import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { take, catchError, map, first } from 'rxjs/operators';
import { BehaviorSubject, throwError } from 'rxjs';
import {
  CatalogListItemResponse as CatalogItem,
  PreorderAvailabilityStatus as PreorderAvailability,
} from '@medindex-webapi';

import { AddCartItemService } from '@shared/modules/add-cart-item/services/add-cart-item.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { ModalSize } from '@shared/enums/modal-sizes';
import { IServiceInfo } from '@shared/modules/modal-appointment-doctor/interfaces/modal-appointment-doctor.interfaces';
import { StorageService } from '@shared/core/services/storage.service';
import { ModalAppointmentDoctorComponent } from '@shared/modules/modal-appointment-doctor/modal-appointment-doctor.component';
import { AddToCartMessages } from '@shared/modules/add-cart-item/const/messages';
import { ModalErrorType } from '@shared/enums/modal-error-type';
import { WarningModalComponent } from '@shared/components/warning-modal/warning-modal.component';
import { ModalTitle } from '@shared/enums/modal-title.enum';
import { PreventDoubleclickDirective } from '@shared/directives/prevent-doubleclick.directive';
import { YmService } from '@shared/services/yandex-metrica/ym.service';
import { YmGoalAppointment } from '@shared/services/yandex-metrica/enums/appointment.goals';
import { SStrikeColorDirective } from '@shared/directives/s.strike-color.directive';
import { TypeNomenclaturePipe } from '../../pipes/type-nomenclature';
import { YmGoalCatalog } from '../../enums/ym-goal-catalog.enum';
import { YmGoalCatalogItem } from '../../enums/ym-goal-catalog-item.enum';
import { NavigationMenuService } from '@shared/services/navigation-menu.service';

@Component({
  selector: 'app-catalog-list-item',
  templateUrl: './catalog-list-item.component.html',
  styleUrls: ['./catalog-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TypeNomenclaturePipe,
    PreventDoubleclickDirective,
    RouterLink,
    SStrikeColorDirective,
  ],
})
export class CatalogListItemComponent {
  constructor(
    private addCartItemService: AddCartItemService,
    private modalService: ModalService,
    private storage: StorageService,
    private ymService: YmService,
    private navigationMenu: NavigationMenuService
  ) {}

  @Input() item!: CatalogItem;

  cityName$ = this.storage.cityName$;

  isInCart$ = this.addCartItemService.hxids$.pipe(
    map((hxids) => hxids.includes(this.item.hxid || ''))
  );
  isLoading$ = new BehaviorSubject(false);

  protected readonly YmGoalCatalogItem = YmGoalCatalogItem;

  tryAddItemToCart(event: Event): void {
    if (this.item.canPreOrder) {
      this._addToCart();
    } else {
      if (this.item.preorderAvailability === PreorderAvailability.Appointment) {
        this.openModalAppointmentDoctor();
      }

      if (
        this.item.preorderAvailability ===
        PreorderAvailability.OnlyInDiagnosticCenter
      ) {
        this.openInfoModal();
      }
    }
    event.preventDefault();
    event.stopPropagation();
  }

  private openModalAppointmentDoctor(): void {
    const { price, currency, hxid, title } = this.item;

    this.storage.cityName$.pipe(first()).subscribe((cityName) => {
      const serviceInfo: IServiceInfo = {
        price: price.toString(),
        currency: currency?.symbol || null,
        cityName,
        hxid: hxid || '',
        description: title || '',
      };

      this.modalService.create({
        nzTitle: ModalTitle.MakeAppointment,
        nzContent: ModalAppointmentDoctorComponent,
        nzStyle: {
          width: ModalSize.Form,
        },
        nzClassName: 'modal-with-bg',
        nzComponentParams: {
          serviceInfo,
        },
        nzFooter: null,
      });
      this.ymService.reachGoal(
        YmGoalAppointment.CatalogDoctorAppointmentModalOpen
      );
    });
  }

  private openInfoModal(): void {
    this.navigationMenu.isHelixLiteAvailable$
      .pipe(first())
      .subscribe((isHelixLite) => {
        this.modalService.create({
          nzContent: WarningModalComponent,
          nzComponentParams: {
            content: isHelixLite
              ? AddToCartMessages.CannotPreorderHelixLiteMessage
              : AddToCartMessages.CannotPreorderMessage,
            errorType: ModalErrorType.Info,
          },
          nzStyle: { width: ModalSize.Info },
          nzFooter: null,
        });
      });
  }

  private _addToCart(): void {
    this.isLoading$.next(true);

    this.addCartItemService
      .addItemWithConfirmation(
        this.item.hxid || '',
        YmGoalCatalog.ItemListAddToCart
      )
      .pipe(
        take(1),
        catchError((error) => {
          this.isLoading$.next(false);
          return throwError(() => new Error(error));
        })
      )
      .subscribe((): void => {
        this.isLoading$.next(false);
      });
  }
}
